import { useState, useEffect } from "react";
import { Plus, AlertTriangle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { usePagination } from "@/hooks/usePagination";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { ProposalSummaryCards } from "@/components/proposals/ProposalSummaryCards";
import { ProposalFilters } from "@/components/proposals/ProposalFilters";
import { ProposalTable } from "@/components/proposals/ProposalTable";
import { ProposalForm } from "@/components/proposals/ProposalForm";
import { ProposalDetails } from "@/components/proposals/ProposalDetails";
import { DeleteConfirmDialog } from "@/components/proposals/DeleteConfirmDialog";
import { DateRangeProvider, useDateRange } from "@/components/dashboard/DateRangeContext";
import { DateRangeFilter } from "@/components/dashboard/DateRangeFilter";

function ProposalsContent() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedClient, setSelectedClient] = useState("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<any>(null);
  const [editingProposal, setEditingProposal] = useState<any>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<any>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { dateRange } = useDateRange();

  // Role-based access control
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>You must be logged in to access this page.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Fetch proposals with server-side pagination and date filtering
  const { data: proposals, isLoading, error } = useQuery({
    queryKey: ["proposals", dateRange.from, dateRange.to],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposals")
        .select(`
          *,
          clients:client_id(name, id),
          profiles:created_by(first_name, last_name, full_name)
        `)
        .gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString())
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Summary metrics for cards
  const allProposals = (proposals || []) as any[];
  const toNumber = (n: any) => Number(n ?? 0);
  const baseValue = (p: any) => toNumber(p?.final_value ?? p?.value);
  const sumBase = (arr: any[]) => arr.reduce((s, p) => s + baseValue(p), 0);
  const sumExpected = (arr: any[]) =>
    arr.reduce((s, p) => {
      const prob = toNumber(p?.probability ?? 100);
      return s + (baseValue(p) * prob) / 100;
    }, 0);

  const totalSentCount = allProposals.filter(p => p.sent_date).length;
  const totalSentValue = sumBase(allProposals.filter(p => p.sent_date));

  const acceptedArr = allProposals.filter(
    (p) => (p.status || "").toLowerCase() === "accepted"
  );
  const acceptedCount = acceptedArr.length;
  const acceptedValue = sumBase(acceptedArr);

  const today = new Date();
  const pendingArr = allProposals.filter((p) => {
    const status = (p.status || "").toLowerCase();
    return status === "pending";
  });
  const pendingCount = pendingArr.length;
  const pendingValue = sumBase(pendingArr);

  const rejectedArr = allProposals.filter(
    (p) => (p.status || "").toLowerCase() === "rejected"
  );
  const rejectedCount = rejectedArr.length;
  const rejectedValue = sumBase(rejectedArr);


  // Fetch clients for dropdown
  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });

  // Delete proposal mutation
  const deleteProposalMutation = useMutation({
    mutationFn: async (proposalId: string) => {
      // First, update any projects that reference this proposal to set proposal_id to null
      const { error: projectUpdateError } = await supabase
        .from("projects")
        .update({ proposal_id: null })
        .eq("proposal_id", proposalId);

      if (projectUpdateError) throw projectUpdateError;

      // Then delete the proposal
      const { error } = await supabase
        .from("proposals")
        .delete()
        .eq("id", proposalId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      queryClient.invalidateQueries({ queryKey: ["proposal-statistics"] });
      setIsDeleteDialogOpen(false);
      setProposalToDelete(null);
      toast({
        title: "Success",
        description: "Proposal deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete proposal",
        variant: "destructive",
      });
    },
  });

  // Filter proposals
  const filteredProposals = proposals?.filter(proposal => {
    const matchesSearch = proposal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proposal.clients?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Use the actual proposal status logic that considers sent_date
    const actualStatus = !proposal.sent_date ? 'draft' : proposal.status;
    const matchesStatus = selectedStatus === "all" || actualStatus === selectedStatus;
    const matchesClient = selectedClient === "all" || proposal.client_id === selectedClient;
    
    return matchesSearch && matchesStatus && matchesClient;
  }) || [];

  // Pagination
  const {
    items: paginatedProposals,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    goToPage,
    nextPage,
    prevPage,
    resetPagination
  } = usePagination(filteredProposals, 100);

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [searchTerm, selectedStatus, selectedClient]);

  // Action handlers
  const handleViewProposal = (proposal: any) => {
    setSelectedProposal(proposal);
    setIsDetailsModalOpen(true);
  };

  const handleEditProposal = (proposal: any) => {
    setEditingProposal(proposal);
    setIsAddModalOpen(true);
  };

  const handleDeleteProposal = (proposal: any) => {
    setProposalToDelete(proposal);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (proposalToDelete) {
      deleteProposalMutation.mutate(proposalToDelete.id);
    }
  };

  const handleCloseForm = () => {
    setIsAddModalOpen(false);
    setEditingProposal(null);
  };

  const handleCloseDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedProposal(null);
  };

  // Loading skeleton
  const ProposalSkeleton = () => (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-10 w-32" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="space-y-4">
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-0">
            <div className="space-y-2 p-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  if (isLoading) {
    return <ProposalSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load proposals: {error.message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Proposals</h1>
        <div className="flex items-center gap-4">
          <DateRangeFilter />
          <Button 
            onClick={() => {
              setEditingProposal(null);
              setIsAddModalOpen(true);
            }}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Proposal
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <ProposalSummaryCards
        totalSentCount={totalSentCount}
        totalSentValue={Number(totalSentValue || 0)}
        acceptedCount={acceptedCount}
        acceptedValue={Number(acceptedValue || 0)}
        pendingCount={pendingCount}
        pendingValue={Number(pendingValue || 0)}
        rejectedCount={rejectedCount}
        rejectedValue={Number(rejectedValue || 0)}
      />

      {/* Filters */}
      <ProposalFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
        clients={clients || []}
      />

      {/* Proposals Table */}
      <ProposalTable 
        proposals={paginatedProposals}
        isLoading={isLoading}
        onView={handleViewProposal}
        onEdit={handleEditProposal}
        onDelete={handleDeleteProposal}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    prevPage();
                  }}
                  className={!hasPrevPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      goToPage(page);
                    }}
                    isActive={currentPage === page}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              
              <PaginationItem>
                <PaginationNext 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    nextPage();
                  }}
                  className={!hasNextPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Proposal Form Dialog */}
      <ProposalForm
        open={isAddModalOpen}
        onOpenChange={handleCloseForm}
        proposal={editingProposal}
        clients={clients || []}
      />

      {/* Proposal Details Modal */}
      <ProposalDetails
        proposal={selectedProposal}
        open={isDetailsModalOpen}
        onOpenChange={handleCloseDetails}
        onEdit={() => {
          setEditingProposal(selectedProposal);
          setIsDetailsModalOpen(false);
          setIsAddModalOpen(true);
        }}
        onDelete={() => {
          setIsDetailsModalOpen(false);
          handleDeleteProposal(selectedProposal);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={confirmDelete}
        proposalTitle={proposalToDelete?.title || ""}
      />
    </div>
  );
}

export default function Proposals() {
  return (
    <DateRangeProvider>
      <ProposalsContent />
    </DateRangeProvider>
  );
}